import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useSnackbar } from 'notistack';
import { useApiQuery } from '../../../../../api/hooks';
import apiClient from '../../../../../api/apiClient';
import { usePreloaderStore } from '../../../../../stores';
import useDozorEditState from '../../../../../hooks/useDozorEditState';
import DozorData from '../../../../../component/dozor/DozorData';
import { RefreshIcon, PlusIcon, ImageIcon, TrashIcon } from '../../../model/icons';
import GameHeader from '../GameHeader';
import StatusRow from '../StatusRow';
import DozorCard from './DozorCard';
import PointModal from './PointModal';
import TransitionModal from './TransitionModal';
import '../games.css';
import './dozor.css';

const DOZOR_STATUSES = [
    { value: 'DEVELOP', label: 'В разработке', tone: 'dev' },
    { value: 'WAITING_ACTIVE', label: 'Ожидание', tone: 'wait' },
    { value: 'ACTIVE', label: 'Активна', tone: 'active' },
    { value: 'FINISHED', label: 'Завершена', tone: 'done' }
];

// Игра «Дозор» (без макета — по дизайн-системе): база, последовательность, точки, переходы.
export default function DozorGame({ shift }) {
    const smenaId = shift.id;
    const { data: dozor, isLoading, error } = useApiQuery(
        ['dozor', 'edit', smenaId],
        `/api/dozor/admin/games/find-by-smena?smenaId=${smenaId}`,
        { refetchOnMount: 'always', enabled: !!smenaId }
    );

    if (error) return <div className="vp-empty">{error.response?.data?.message || 'Ошибка загрузки игры «Дозор»'}</div>;
    if (isLoading) return <div className="vp-empty">Загрузка…</div>;
    if (!dozor) return null;

    return <DozorContent dozor={dozor} shift={shift} />;
}

DozorGame.propTypes = {
    shift: PropTypes.shape({ id: PropTypes.number.isRequired, title: PropTypes.string }).isRequired
};

function DozorContent({ dozor, shift }) {
    const dozorState = useDozorEditState(dozor.id);
    const { enqueueSnackbar } = useSnackbar();
    const { show, hide } = usePreloaderStore();
    const [status, setStatus] = useState(dozor.status || dozor.gameStatus || null);
    const [pointId, setPointId] = useState(null);
    const [transitionId, setTransitionId] = useState(null);

    const changeStatus = async (newStatus) => {
        show();
        try {
            await apiClient.post(`/api/dozor/admin/controls/update-status?dozorId=${dozor.id}&status=${newStatus}`);
            setStatus(newStatus);
            enqueueSnackbar('Статус обновлён', { variant: 'success' });
        } catch (e) { enqueueSnackbar(e.response?.data?.message || 'Ошибка при смене статуса', { variant: 'error' }); }
        finally { hide(); }
    };

    const meta = (
        <>
            <span><b>{dozorState.points.length}</b> точек</span>
            <span className="dot">·</span>
            <span><b>{dozorState.transitions.length}</b> переходов</span>
        </>
    );

    return (
        <>
            <GameHeader
                title={`Дозор — ${shift.title || ''}`}
                meta={meta}
                status={<StatusRow statuses={DOZOR_STATUSES} current={status} onChange={changeStatus} />}
            />
            <section className="content white">
                <DozorBaseParams dozor={dozor} />

                <div className="ev-head dz-section">
                    <h2>Последовательность</h2>
                    <button type="button" className="btn btn-ghost" onClick={dozorState.refreshTeams}><span className="ic"><RefreshIcon /></span>Обновить команды</button>
                </div>
                <DozorData dozor={dozor} accessType="EDIT" dozorState={dozorState} />

                <div className="ev-head dz-section">
                    <h2>Точки</h2>
                    <button type="button" className="btn btn-primary" onClick={dozorState.createPoint}><span className="ic"><PlusIcon /></span>Добавить точку</button>
                </div>
                {dozorState.points.length > 0 ? (
                    <div className="dz-grid">
                        {dozorState.points.map(p => (
                            <DozorCard key={p.id} num={p.orderId} title={p.title} meta={<span>{p.time} · ошибка: {p.incorrectCodeCost}</span>} onClick={() => setPointId(p.id)} />
                        ))}
                    </div>
                ) : <div className="bt-empty">Нет точек</div>}

                <div className="ev-head dz-section">
                    <h2>Переходы</h2>
                    <button type="button" className="btn btn-primary" onClick={dozorState.createTransition}><span className="ic"><PlusIcon /></span>Добавить переход</button>
                </div>
                {dozorState.transitions.length > 0 ? (
                    <div className="dz-grid">
                        {dozorState.transitions.map(t => (
                            <DozorCard key={t.id} num={t.orderId} title={t.title} onClick={() => setTransitionId(t.id)} />
                        ))}
                    </div>
                ) : <div className="bt-empty">Нет переходов</div>}
            </section>

            {pointId && <PointModal pointId={pointId} dozorState={dozorState} onClose={() => setPointId(null)} />}
            {transitionId && <TransitionModal transitionId={transitionId} dozorState={dozorState} onClose={() => setTransitionId(null)} />}
        </>
    );
}

DozorContent.propTypes = {
    dozor: PropTypes.object.isRequired,
    shift: PropTypes.shape({ id: PropTypes.number.isRequired, title: PropTypes.string }).isRequired
};

function DozorBaseParams({ dozor }) {
    const { enqueueSnackbar } = useSnackbar();
    const { show, hide } = usePreloaderStore();
    const [form, setForm] = useState({ title: dozor.title || '', firstText: dozor.firstText || '', lastText: dozor.lastText || '' });
    const [imgUrl, setImgUrl] = useState(dozor.media?.mediaFileUrl || null);
    const [pendingFile, setPendingFile] = useState(null);
    const [removed, setRemoved] = useState(false);
    const [preview, setPreview] = useState(null);

    useEffect(() => {
        if (!pendingFile) { setPreview(null); return undefined; }
        const url = URL.createObjectURL(pendingFile);
        setPreview(url);
        return () => URL.revokeObjectURL(url);
    }, [pendingFile]);

    const set = (field) => (e) => setForm(prev => ({ ...prev, [field]: e.target.value }));

    const pick = () => {
        const input = document.createElement('input');
        input.type = 'file';
        input.accept = 'image/*';
        input.onchange = (e) => { if (e.target.files[0]) { setPendingFile(e.target.files[0]); setRemoved(false); } };
        input.click();
    };
    const remove = () => { setPendingFile(null); setRemoved(true); setImgUrl(null); };

    const save = async () => {
        show();
        try {
            const fd = new FormData();
            const payload = { title: form.title, firstText: form.firstText, lastText: form.lastText };
            if (pendingFile) payload.media = {};
            else if (removed) payload.media = null;
            fd.append('dozor', JSON.stringify(payload));
            if (pendingFile) fd.append('newFile', pendingFile);
            const resp = await apiClient.put(`/api/dozor/admin/games/save/${dozor.id}`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
            enqueueSnackbar('Сохранено', { variant: 'success' });
            const d = resp.data;
            if (d) { setImgUrl(d.media?.mediaFileUrl || null); setPendingFile(null); setRemoved(false); }
        } catch (e) { enqueueSnackbar('Ошибка при сохранении', { variant: 'error' }); }
        finally { hide(); }
    };

    const shownUrl = preview || imgUrl;

    return (
        <section className="fr-card">
            <div className="dz-base">
                {shownUrl ? (
                    <div className="dz-imgbox">
                        <img src={shownUrl} alt="" />
                        <div className="acts"><button type="button" onClick={remove} title="Удалить"><TrashIcon /></button></div>
                    </div>
                ) : (
                    <div className="dz-imgbox" role="button" tabIndex={0} onClick={pick} onKeyDown={(e) => { if (e.key === 'Enter') pick(); }}>
                        <ImageIcon />
                        <span style={{ fontSize: 13, color: 'var(--muted)' }}>Загрузить изображение</span>
                    </div>
                )}
                <div className="dz-msgs">
                    <div><label className="ctl-label">Название</label><input className="input" value={form.title} onChange={set('title')} /></div>
                    <div><label className="ctl-label">Начало игры</label><textarea className="textarea" value={form.firstText} onChange={set('firstText')} /></div>
                    <div><label className="ctl-label">Конец игры</label><textarea className="textarea" value={form.lastText} onChange={set('lastText')} /></div>
                </div>
            </div>
            <div style={{ display: 'flex', justifyContent: 'center', marginTop: 18 }}>
                <button type="button" className="btn btn-primary" onClick={save} style={{ minWidth: 200 }}>Сохранить</button>
            </div>
        </section>
    );
}

DozorBaseParams.propTypes = { dozor: PropTypes.object.isRequired };
